/**
 * Traces: read back the agent turns you shipped with `morphTracing()`, each
 * carrying the Reflex labels that ran on it.
 *
 *   const morph = new MorphClient({ apiKey });
 *   const page  = await morph.traces.list({ convoId: 'c1' });
 *   for (const turn of page.data) {
 *     console.log(turn.inputText, turn.reflexResults);
 *   }
 *
 * Reads `GET /v1/reflex/traces`. All HTTP goes through the shared `MorphAPIClient`.
 */
import { APIResource } from '../../core/resource.js';
import type { ListTracesInput, Trace, TraceList, TraceReflexResult } from './types.js';

export class TracesClient extends APIResource {
  /** List traced turns, newest first. Filter by `convoId`; page with `limit`/`offset`. */
  async list(input: ListTracesInput = {}): Promise<TraceList> {
    const raw = await this._client.get<RawTraceList>('/v1/reflex/traces', {
      query: { convo_id: input.convoId, limit: input.limit, offset: input.offset },
    });
    return {
      data: (raw.data ?? []).map(toTrace),
      hasMore: Boolean(raw.has_more),
      offset: raw.offset ?? 0,
    };
  }
}

// ---------------------------------------------------------------------------
// Mapping (snake_case -> camelCase)
// ---------------------------------------------------------------------------

interface RawReflexResult {
  model: string;
  transform: string;
  label: string | null;
  score: number;
  selected?: string[];
  status: string;
  transform_version: number;
}
interface RawTrace {
  convo_id: string;
  event_id: string;
  span_kind: string;
  model: string;
  input_text: string;
  output_text: string;
  start_time: string;
  reflex_results?: RawReflexResult[];
}
interface RawTraceList {
  data?: RawTrace[];
  has_more?: boolean;
  offset?: number;
}

function toReflexResult(raw: RawReflexResult): TraceReflexResult {
  return {
    model: raw.model,
    transform: raw.transform,
    label: raw.label ?? null,
    score: raw.score,
    selected: raw.selected ?? [],
    status: raw.status,
    transformVersion: raw.transform_version,
  };
}

function toTrace(raw: RawTrace): Trace {
  return {
    convoId: raw.convo_id,
    eventId: raw.event_id,
    spanKind: raw.span_kind,
    model: raw.model,
    inputText: raw.input_text,
    outputText: raw.output_text,
    startTime: raw.start_time,
    reflexResults: (raw.reflex_results ?? []).map(toReflexResult),
  };
}
